/**
 * W1-11b (app-shell-ui): the application root (ARCHITECTURE.md §3.2): the shell context, the UiBridge every
 * primitive uses (keymap labels, registry state and runs, translation), the hash router, the global drop zone,
 * the document preferences (theme, reduced motion, UI scale, level, locale) and the global overlays (dialog host,
 * palette, shortcut sheet, context menus, toasts) inside the app's error boundary.
 *
 * Drop anywhere (DESIGN.md §4.1, §5.12): files dragged over the window show the drop overlay; on drop the registered
 * `FileDropHandler`s are asked by descending priority and the first that accepts handles the files (W2-09 imports
 * songs, charts, packages and skins). Dropped folders are expanded into their files (recursively; each carries its
 * path below the drop as `webkitRelativePath`, like a folder picked with `<input webkitdirectory>`; droppedFiles.ts),
 * so a whole RPE Resources/ folder reaches the handlers in one call. The browser never navigates away to a dropped
 * file.
 */

import { memo, useEffect, useMemo, useState, type ReactNode } from 'react';
import { UiBridgeProvider } from '../../ui/bridge';
import { Icon } from '../../ui/Icon';
import { applyUiPreferences } from '../../ui/preferences';
import type { AppServices, WhenContext } from '../registry/types';
import { DialogHost } from '../dialogs/DialogHost';
import { createUiBridge, createUiService, isShellUiService, type ShellUiService } from '../uiService';
import { CheatSheet } from './CheatSheet';
import { currentWhen } from './commandState';
import { ContextMenuHost } from './ContextMenuHost';
import { expandDropEntries, takeDropEntries, type DropSource } from './droppedFiles';
import { errorText } from './download';
import { ErrorBoundary } from './ErrorBoundary';
import { Palette } from './Palette';
import { Router } from './Router';
import { dependsOn, ShellProvider, useSettingsValue, useText, type ShellEnv, type ShellFocus } from './ShellContext';
import { Toasts } from './Toasts';
import { m } from './shell.messages';
import s from './Shell.module.css';

/** The keyboard router's part the shell uses (src/app/shell/KeyboardRouter.ts). */
export interface ShellRouter {
  whenContext(): WhenContext;
  setAtom(atom: string, on: boolean): void;
}

export interface AppProps {
  readonly services: AppServices;
  /** The shell UI service (default: `services.ui` when it is one). */
  readonly ui?: ShellUiService;
  readonly router?: ShellRouter | null;
  readonly focus?: ShellFocus | null;
}

const noop = (): void => undefined;

/** Bumps (at most once per frame) when anything a command's enabled state may depend on changes. */
function useBridgeVersion(services: AppServices): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let frame = 0;
    const bump = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setVersion((v) => v + 1);
      });
    };
    const offs: Array<() => void> = [];
    let storeOffs: Array<() => void> = [];
    const watchStore = (): void => {
      for (const off of storeOffs) off();
      storeOffs = [];
      const store = services.store;
      if (store !== null) storeOffs = [store.subscribeCommits(bump), store.history.subscribe(bump)];
      bump();
    };
    watchStore();
    offs.push(services.onChange(watchStore));
    offs.push(
      services.editor.subscribe((st, prev) => {
        if (
          st.selection !== prev.selection ||
          st.activeLine !== prev.activeLine ||
          st.tool !== prev.tool ||
          st.placement !== prev.placement ||
          st.workspace !== prev.workspace ||
          st.focusedColumn !== prev.focusedColumn ||
          st.loop !== prev.loop ||
          st.metronome !== prev.metronome ||
          st.returnOnStop !== prev.returnOnStop ||
          st.snap !== prev.snap ||
          st.preview !== prev.preview ||
          st.filters !== prev.filters ||
          st.projectId !== prev.projectId ||
          st.timeline.showMotionColumns !== prev.timeline.showMotionColumns ||
          st.timeline.waveform !== prev.timeline.waveform ||
          st.timeline.followPlayhead !== prev.timeline.followPlayhead
        ) {
          bump();
        }
      }),
    );
    offs.push(services.settings.subscribe(bump));
    offs.push(services.keymap.subscribe(bump));
    offs.push(services.audio.transport.onChange(bump));
    const reg = services.registry as Partial<{ onChange(cb: () => void): () => void }>;
    if (typeof reg.onChange === 'function') offs.push(reg.onChange(bump));
    if (isShellUiService(services.ui)) offs.push(services.ui.subscribe(bump));
    return () => {
      cancelAnimationFrame(frame);
      for (const off of [...offs, ...storeOffs]) off();
    };
  }, [services]);
  return version;
}

/** Applies theme, reduced motion, UI scale and level to <html>, and the locale to i18n, whenever they change. */
function usePreferences(services: AppServices): void {
  const g = useSettingsValue(services).general;
  useEffect(() => {
    applyUiPreferences({ theme: g.theme, reducedMotion: g.reducedMotion, uiScale: g.uiScale, level: g.level });
  }, [g.theme, g.reducedMotion, g.uiScale, g.level]);
  useEffect(() => {
    if (services.i18n.locale !== g.locale) services.i18n.setLocale(g.locale);
  }, [g.locale, services]);
}

function hasFiles(e: DragEvent): boolean {
  return e.dataTransfer?.types.includes('Files') === true;
}

/** Hands dropped files to the first registered handler that accepts them. */
export async function handleDrop(services: AppServices, files: readonly File[]): Promise<boolean> {
  if (files.length === 0) return false;
  const handler = services.registry.fileDropHandlers(services).find((h) => {
    try {
      return h.accept(files);
    } catch {
      return false;
    }
  });
  if (handler === undefined) {
    services.ui.toast({ key: m.toastDropNone, params: { count: files.length } }, { kind: 'warning' });
    return false;
  }
  try {
    await handler.handle(files);
    return true;
  } catch (err) {
    services.ui.toast(
      { key: m.toastDropFailed, params: { error: errorText(err, services.i18n.t(m.unknownError)) } },
      { kind: 'error' },
    );
    return false;
  }
}

/** Expands a drop (folders included) and hands its files to the handlers; reports empty or cut-short folders. */
export async function dropSources(services: AppServices, sources: readonly DropSource[]): Promise<boolean> {
  if (sources.length === 0) return false;
  let dropped;
  try {
    dropped = await expandDropEntries(sources);
  } catch (err) {
    services.ui.toast(
      { key: m.toastDropFailed, params: { error: errorText(err, services.i18n.t(m.unknownError)) } },
      { kind: 'error' },
    );
    return false;
  }
  if (dropped.truncated) {
    services.ui.toast({ key: m.toastDropTruncated, params: { count: dropped.files.length } }, { kind: 'warning' });
  }
  if (dropped.files.length === 0) {
    if (dropped.folders) services.ui.toast({ key: m.toastDropEmpty }, { kind: 'warning' });
    return false;
  }
  return handleDrop(services, dropped.files);
}

function DropZone(props: { readonly services: AppServices; readonly children: ReactNode }) {
  const { services } = props;
  const text = useText(services);
  const [active, setActive] = useState(false);
  useEffect(() => {
    let depth = 0;
    const onEnter = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      depth++;
      setActive(true);
    };
    const onOver = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('input[type="file"]') != null) return;
      e.preventDefault();
      if (e.dataTransfer !== null) e.dataTransfer.dropEffect = 'copy';
    };
    const onLeave = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0 || e.relatedTarget === null) {
        depth = 0;
        setActive(false);
      }
    };
    const onDrop = (e: DragEvent): void => {
      depth = 0;
      setActive(false);
      if (!hasFiles(e)) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('input[type="file"]') != null) return;
      e.preventDefault();
      // The entries must be taken now: the browser empties the DataTransfer once the event is over.
      const sources = takeDropEntries(e.dataTransfer);
      void dropSources(services, sources);
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [services]);
  return (
    <>
      {props.children}
      {active && (
        <div className={s.dropOverlay} aria-hidden="true">
          <div className={s.dropCard}>
            <Icon name="FileInput" size={32} />
            <p className={s.dropTitle}>{text.t(m.dropTitle)}</p>
            <p className={s.dropBody}>{text.t(m.dropBody)}</p>
          </div>
        </div>
      )}
    </>
  );
}

/** Features' always-mounted overlays (FeatureModule.overlays), each in its own silent error boundary. */
function ModuleOverlays(props: { readonly services: AppServices }) {
  const { services } = props;
  return (
    <>
      {services.registry.overlays().map((o) => (
        <ErrorBoundary key={o.id} variant="silent" services={services} label={o.id}>
          <o.component services={services} />
        </ErrorBoundary>
      ))}
    </>
  );
}

/**
 * Pages and overlays. Memoised on the (stable) environment: a bridge refresh (every selection change, commit,
 * settings change, transport change... at most once per frame) reaches only the components that read the bridge
 * (their context changes), not the whole route tree; each part follows its own topics (DESIGN.md §10).
 */
const ShellBody = memo(function ShellBody(props: { readonly env: ShellEnv }) {
  const { services, ui, setAtom } = props.env;
  return (
    <div className={s.app}>
      <DropZone services={services}>
        <Router services={services} />
      </DropZone>
      <ModuleOverlays services={services} />
      <DialogHost services={services} ui={ui} setAtom={setAtom} />
      <Palette services={services} ui={ui} />
      <CheatSheet services={services} ui={ui} />
      <ContextMenuHost services={services} ui={ui} />
      <Toasts services={services} ui={ui} />
    </div>
  );
});

function Shell(props: { readonly env: ShellEnv }) {
  const { services } = props.env;
  usePreferences(services);
  const version = useBridgeVersion(services);
  const when = props.env.when;
  const bridge = useMemo(() => {
    dependsOn(version);
    return createUiBridge({ services: () => services, when });
  }, [services, when, version]);
  return (
    <UiBridgeProvider bridge={bridge}>
      <ErrorBoundary variant="app" services={services} label="app">
        <ShellBody env={props.env} />
      </ErrorBoundary>
    </UiBridgeProvider>
  );
}

export function App(props: AppProps) {
  const { services, router, focus } = props;
  const [fallbackUi] = useState<ShellUiService | null>(() =>
    props.ui !== undefined || isShellUiService(services.ui) ? null : createUiService({ services: () => services }),
  );
  const ui = props.ui ?? (isShellUiService(services.ui) ? services.ui : fallbackUi);
  const env = useMemo<ShellEnv | null>(() => {
    if (ui === null) return null;
    const when = router?.whenContext !== undefined ? () => router.whenContext() : () => currentWhen(services);
    return {
      services,
      ui,
      when,
      setAtom:
        router === null || router === undefined
          ? noop
          : (atom, on) => {
              router.setAtom(atom, on);
            },
      focus: focus ?? null,
    };
  }, [services, ui, router, focus]);
  if (env === null) return null;
  return (
    <ShellProvider env={env}>
      <Shell env={env} />
    </ShellProvider>
  );
}
